import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import EventService from '../../services/EventService';
import FeedbackService from '../../services/FeedbackService';
import FeedbackQuestionCard from './components/FeedbackQuestionCard';
import StarRating from './components/StarRating';
import NpsScale from './components/NpsScale';
import MultiSelectGrid from './components/MultiSelectGrid';
import RadioCardGroup from './components/RadioCardGroup';
import PillButtonGroup from './components/PillButtonGroup';
import FeedbackSuccess from './components/FeedbackSuccess';
import './EventFeedback.css';

const HIGHLIGHT_OPTIONS = [
  'Content depth & research',
  'Presenter knowledge',
  'Live Q&A session',
  'Practical examples',
  'Slide design & clarity',
  'Actionable takeaways',
];

const PRESENTER_OPTIONS = [
  {
    value: 'excellent',
    label: 'Excellent',
    subtitle: 'Clear, engaging, and consistently well-paced',
  },
  {
    value: 'good',
    label: 'Good',
    subtitle: 'Mostly clear with a few minor rough patches',
  },
  {
    value: 'average',
    label: 'Average',
    subtitle: 'Some sections were difficult to follow',
  },
  {
    value: 'needs_improvement',
    label: 'Needs improvement',
    subtitle: 'Often unclear or hard to stay engaged',
  },
];

const LENGTH_OPTIONS = ['Too short', 'Just right', 'Slightly too long', 'Way too long'];

const BENEFIT_OPTIONS = [
  {
    value: 'extremely',
    label: 'Extremely beneficial',
    subtitle: 'Directly solved my problem or answered my need',
  },
  {
    value: 'very',
    label: 'Very beneficial',
    subtitle: 'Gave me clear direction and actionable next steps',
  },
  {
    value: 'somewhat',
    label: 'Somewhat beneficial',
    subtitle: "Helpful, but didn't fully address my situation",
  },
  {
    value: 'slightly',
    label: 'Slightly beneficial',
    subtitle: 'A few useful points but mostly not relevant to me',
  },
  {
    value: 'not',
    label: 'Not beneficial',
    subtitle: "Didn't address my issue at all",
  },
];

const PRESENTER_API_MAP = {
  excellent: 'Excellent',
  good: 'Good',
  average: 'Average',
  needs_improvement: 'Needs improvement',
};

const BENEFIT_API_MAP = {
  extremely: 'Extremely beneficial',
  very: 'Very beneficial',
  somewhat: 'Somewhat beneficial',
  slightly: 'Slightly beneficial',
  not: 'Not beneficial',
};

const API_FIELD_TO_FORM = {
  eventId: 'submit',
  EventId: 'submit',
  overallSatisfaction: 'starRating',
  recommendLikelihood: 'npsScore',
  valuableTopics: 'highlights',
  presenterDelivery: 'presenterDelivery',
  webinarLength: 'webinarLength',
  benefitLevel: 'benefitLevel',
  suggestions: 'suggestions',
  'respondent.email': 'submit',
  'Respondent.Email': 'submit',
  businessAdminId: 'submit',
};

const mapApiErrors = (errors) => {
  const mapped = {};
  if (!Array.isArray(errors)) return mapped;

  errors.forEach((entry) => {
    Object.entries(entry).forEach(([key, message]) => {
      const formKey = API_FIELD_TO_FORM[key] || key;
      mapped[formKey] = message;
    });
  });

  return mapped;
};

const getRespondentFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    name: params.get('name')?.trim() || '',
    email: params.get('email')?.trim() || '',
  };
};

const buildPayload = (form, eventId, respondent) => {
  const payload = {
    eventId,
    respondent: {
      name: respondent.name,
      email: respondent.email,
    },
    overallSatisfaction: form.starRating,
    recommendLikelihood: form.npsScore,
    presenterDelivery: PRESENTER_API_MAP[form.presenterDelivery],
    webinarLength: form.webinarLength,
    benefitLevel: BENEFIT_API_MAP[form.benefitLevel],
  };

  if (form.highlights.length > 0) {
    payload.valuableTopics = form.highlights;
  }

  const suggestions = form.suggestions?.trim();
  if (suggestions) {
    payload.suggestions = suggestions;
  }

  return payload;
};

const DEFAULT_SUBTITLE = 'Advanced Email Marketing Strategies · June 20, 2026';

const SWEET_ALERT_CONFIRM_COLOR = 'rgb(208 125 63)';

const confirmResubmitFeedback = async () => {
  const result = await Swal.fire({
    icon: 'question',
    title: 'Already submitted',
    text: 'You have already submitted your feedback. Would you like to submit it again?',
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonColor: '#3B5BDB',
    cancelButtonColor: SWEET_ALERT_CONFIRM_COLOR,
    reverseButtons: true,
  });

  return result.isConfirmed;
};

const formatEventDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const EventFeedback = () => {
  const { eventId } = useParams();
  const [loading, setLoading] = useState(Boolean(eventId));
  const [eventSubtitle, setEventSubtitle] = useState(DEFAULT_SUBTITLE);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const [form, setForm] = useState({
    starRating: 0,
    npsScore: 0,
    highlights: [],
    presenterDelivery: '',
    webinarLength: '',
    benefitLevel: '',
    suggestions: '',
  });

  useEffect(() => {
    if (!eventId) return undefined;

    const fetchEvent = async () => {
      try {
        const businessAdminId = new URLSearchParams(window.location.search).get('businessAdminId');
        const resp = await EventService.fetchEventForRegistration(eventId, businessAdminId);

        if (resp.data?.success) {
          const ev = resp.data.data;
          const name = ev.eventName || 'Event';
          const date = formatEventDate(ev.startDate || ev.endDate);
          setEventSubtitle(date ? `${name} · ${date}` : name);
        }
      } catch (err) {
        console.error('Failed to load event details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  useEffect(() => {
    const checkExistingFeedback = async () => {
      const businessAdminId = new URLSearchParams(window.location.search).get('businessAdminId');
      const { email } = getRespondentFromUrl();

      if (!eventId || !businessAdminId || !email) return;

      try {
        const resp = await FeedbackService.checkAlreadySubmitted(eventId, email, businessAdminId);
        if (resp.data?.success && resp.data?.data?.alreadySubmitted) {
          setAlreadySubmitted(true);
        }
      } catch (err) {
        console.error('Failed to check existing feedback:', err);
      }
    };

    checkExistingFeedback();
  }, [eventId]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validate = () => {
    const nextErrors = {};

    if (!eventId) nextErrors.submit = 'Event ID is missing. Please use a valid feedback link.';
    if (!form.starRating) nextErrors.starRating = 'Please select a star rating.';
    if (!form.npsScore) nextErrors.npsScore = 'Please select a score from 1 to 10.';
    if (!form.presenterDelivery) nextErrors.presenterDelivery = 'Please select an option.';
    if (!form.webinarLength) nextErrors.webinarLength = 'Please select an option.';
    if (!form.benefitLevel) nextErrors.benefitLevel = 'Please select an option.';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submitFeedback = async (businessAdminId) => {
    const payload = buildPayload(form, eventId, getRespondentFromUrl());
    const resp = await FeedbackService.submitFeedback(payload, businessAdminId);

    if (resp.status === 201 && resp.data?.success) {
      setAlreadySubmitted(true);
      setSubmitted(true);
      return;
    }

    setErrors({ submit: resp.data?.message || 'Something went wrong. Please try again.' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const businessAdminId = new URLSearchParams(window.location.search).get('businessAdminId');
    if (!businessAdminId) {
      setErrors({ submit: 'Invalid feedback link. Business admin ID is missing.' });
      return;
    }

    const respondent = getRespondentFromUrl();
    let hasExistingFeedback = alreadySubmitted;

    if (respondent.email) {
      try {
        const checkResp = await FeedbackService.checkAlreadySubmitted(
          eventId,
          respondent.email,
          businessAdminId
        );
        hasExistingFeedback = Boolean(checkResp.data?.data?.alreadySubmitted);
        setAlreadySubmitted(hasExistingFeedback);
      } catch (err) {
        console.error('Failed to verify existing feedback:', err);
      }
    }

    if (hasExistingFeedback) {
      const shouldResubmit = await confirmResubmitFeedback();
      if (!shouldResubmit) return;
    }

    setSubmitting(true);
    setErrors({});

    try {
      await submitFeedback(businessAdminId);
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;

      if (status === 400 && data?.errors) {
        setErrors(mapApiErrors(data.errors));
        return;
      }

      if (status === 404) {
        setErrors({ submit: data?.message || data?.errors?.[0]?.Error || 'Event not found.' });
        return;
      }

      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="event-feedback">
        <div className="ef-loading">Loading...</div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="event-feedback">
        <header className="event-feedback__header">
          <h1 className="event-feedback__title">How was your experience?</h1>
          <p className="event-feedback__subtitle">{eventSubtitle}</p>
        </header>
        <FeedbackSuccess />
      </div>
    );
  }

  return (
    <div className="event-feedback">
      <header className="event-feedback__header">
        <h1 className="event-feedback__title">How was your experience?</h1>
        <p className="event-feedback__subtitle">{eventSubtitle}</p>
      </header>

      <form className="event-feedback__body" onSubmit={handleSubmit} noValidate>
        <FeedbackQuestionCard
          number="Q1"
          label="How would you rate your overall experience?"
          error={errors.starRating}
        >
          <StarRating
            value={form.starRating}
            onChange={(val) => updateField('starRating', val)}
          />
        </FeedbackQuestionCard>

        <FeedbackQuestionCard
          number="Q2"
          label="How likely are you to recommend this webinar to a colleague?"
          error={errors.npsScore}
        >
          <NpsScale
            value={form.npsScore}
            onChange={(val) => updateField('npsScore', val)}
          />
        </FeedbackQuestionCard>

        <FeedbackQuestionCard
          number="Q3"
          label="What did you find most valuable?"
          helper="Select all that apply."
          error={errors.highlights}
        >
          <MultiSelectGrid
            options={HIGHLIGHT_OPTIONS}
            value={form.highlights}
            onChange={(val) => updateField('highlights', val)}
          />
        </FeedbackQuestionCard>

        <FeedbackQuestionCard
          number="Q4"
          label="How would you rate the presenter's delivery?"
          error={errors.presenterDelivery}
        >
          <RadioCardGroup
            name="presenterDelivery"
            options={PRESENTER_OPTIONS}
            value={form.presenterDelivery}
            onChange={(val) => updateField('presenterDelivery', val)}
          />
        </FeedbackQuestionCard>

        <FeedbackQuestionCard
          number="Q5"
          label="How was the length of the webinar?"
          error={errors.webinarLength}
        >
          <PillButtonGroup
            options={LENGTH_OPTIONS}
            value={form.webinarLength}
            onChange={(val) => updateField('webinarLength', val)}
          />
        </FeedbackQuestionCard>

        <FeedbackQuestionCard
          number="Q6"
          label="How beneficial was this webinar for you?"
          error={errors.benefitLevel}
        >
          <RadioCardGroup
            name="benefitLevel"
            options={BENEFIT_OPTIONS}
            value={form.benefitLevel}
            onChange={(val) => updateField('benefitLevel', val)}
          />
        </FeedbackQuestionCard>

        <FeedbackQuestionCard
          number="Q7"
          label="What could we do better next time?"
          helper="Share any suggestions, missing topics, or moments that didn't meet your expectations."
          error={errors.suggestions}
        >
          <textarea
            id="suggestions"
            className={['ef-textarea', errors.suggestions ? 'ef-textarea--error' : ''].filter(Boolean).join(' ')}
            placeholder="e.g. More time for live Q&A, deeper dive into automation workflows..."
            value={form.suggestions}
            onChange={(e) => updateField('suggestions', e.target.value)}
          />
        </FeedbackQuestionCard>

        <div className="ef-submit-wrap">
          <button type="submit" className="ef-submit-btn" disabled={submitting}>
            {submitting ? (
              <>
                <span className="ef-spinner" aria-hidden="true" />
                Submitting...
              </>
            ) : (
              'Submit Feedback'
            )}
          </button>
          {errors.submit && (
            <p className="ef-card__error" role="alert">{errors.submit}</p>
          )}
          <p className="ef-submit-note">
            Your responses are anonymous and help us build better webinars.
          </p>
        </div>
      </form>
    </div>
  );
};

export default EventFeedback;
